import React, { useMemo, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import MetaPanel, { buildFieldMeta } from "./MetaPanel";
import { useSearch } from "../search/SearchContext";
import { useAuth } from "../auth/AuthContext";
import { useLicense } from "../../licensing/LicenseContext";
import DocumentFilings from "../workspace/DocumentFilings";
import HomeDashboard from "../search/HomeDashboard";
import DeliverModal from "../common/DeliverModal";
import { FlowStore } from "../flow/FlowTypes";
import { getFlows } from "../../api/flowApi";
import { getFolders, getDatabases, setDocumentFolder, overrideDocumentClassification,
  getDocumentVersions, checkOutVersion, cancelCheckOutVersion, restoreDocumentVersion, uploadDocumentVersion,
  getWorkflowDefinitions, startWorkflowInstance } from "../../api/adminApi";
import { getWorkflowForDocument } from "../../api/workflowApi";
import FlowIcon from "../icons/FlowIcons";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import RelatedDocuments from "./RelatedDocuments";
import DiscussionPanel from "./DiscussionPanel";
import AnnotationViewer from "./AnnotationViewer";
import ShareModal from "./ShareModal";
import FormatPickerModal from "../search/FormatPickerModal";
import { getApiBase } from "../../utils/env";

/* ─── SVG icons (inline, matching HomeAlternative style) ──────── */
const Icons = {
  back: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
    </svg>
  ),
  doc: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
    </svg>
  ),
  download: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  ),
  compare: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="8" height="18" rx="1"/><rect x="14" y="3" width="8" height="18" rx="1"/>
    </svg>
  ),
  menu: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/>
    </svg>
  ),
  gear: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
    </svg>
  ),
  people: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
    </svg>
  ),
  aiDot: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="5"/></svg>
  ),
  tag: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/>
    </svg>
  ),
  list: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
      <circle cx="4" cy="6" r="1"/><circle cx="4" cy="12" r="1"/><circle cx="4" cy="18" r="1"/>
    </svg>
  ),
  copy: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
  ),
  json: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 3C5 5 5 9 5 12s0 7 3 9"/><path d="M16 3c3 2 3 6 3 9s0 7-3 9"/>
    </svg>
  ),
};

// Case-insensitive getter for any of the provided keys
function getValueCI(obj, keys) {
  if (!obj) return undefined;
  const map = new Map(Object.keys(obj).map((k) => [k.toLowerCase(), k]));
  for (const k of keys) {
    const real = map.get(String(k).toLowerCase());
    if (real) return obj[real];
  }
  return undefined;
}

const fmt = {
  date(v) {
    try {
      const dt = typeof v === "string" ? new Date(v) : v;
      if (!isNaN(dt)) return dt.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
      return String(v);
    } catch {
      return String(v);
    }
  },
  money(v) {
    const n = Number(v);
    return isNaN(n)
      ? String(v)
      : n.toLocaleString(undefined, { style: "currency", currency: "USD" });
  },
  upper(v) {
    return String(v).toUpperCase();
  },
  // File extension: strip leading dot(s) and treat the "-" NOT-NULL placeholder as empty,
  // so ".afp", "afp" and "-" all normalize to a clean upper-case token (or blank).
  fileExt(v) {
    const s = String(v).trim();
    return (s === "-" ? "" : s.replace(/^\.+/, "")).toUpperCase();
  },
};

const MIME_MAP = {
  pdf: "application/pdf",
  html: "text/html",
  htm: "text/html",
  txt: "text/plain",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  bmp: "image/bmp",
  tiff: "image/tiff",
  tif: "image/tiff",
  webp: "image/webp",
  heic: "image/heic",
  heif: "image/heif",
  avif: "image/avif",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  xml: "text/xml",
};

function getDocName(doc) {
  return doc?.originalName || doc?.fileName || doc?.documentName || doc?.name || "Untitled";
}

function getDocDate(doc) {
  return getValueCI(doc, ["documentDate", "date", "createdAt", "created"]);
}

// 0 means "not counted", not "a document with no pages" — see the matching note in ResultsPanel.
function getPageCount(doc) {
  const p = Number(doc?.pageCount ?? doc?.pages);
  return isNaN(p) || p <= 0 ? null : p;
}

function flyToCompareTray(sourceEl) {
  const target = document.querySelector(".compare-tray-badge") || document.querySelector(".results-footer-batch");
  if (!sourceEl || !target) return;
  const srcRect = sourceEl.getBoundingClientRect();
  const tgtRect = target.getBoundingClientRect();
  const ghost = document.createElement("div");
  ghost.className = "compare-fly-ghost";
  ghost.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="8" height="18" rx="1"/><rect x="14" y="3" width="8" height="18" rx="1"/></svg>';
  ghost.style.left = srcRect.left + srcRect.width / 2 - 16 + "px";
  ghost.style.top = srcRect.top + srcRect.height / 2 - 16 + "px";
  ghost.style.setProperty("--fly-dx", (tgtRect.left + tgtRect.width / 2 - (srcRect.left + srcRect.width / 2)) + "px");
  ghost.style.setProperty("--fly-dy", (tgtRect.top + tgtRect.height / 2 - (srcRect.top + srcRect.height / 2)) + "px");
  document.body.appendChild(ghost);
  ghost.addEventListener("animationend", () => {
    ghost.remove();
    const badge = document.querySelector(".compare-tray-badge");
    if (badge) {
      badge.classList.add("pulse");
      setTimeout(() => badge.classList.remove("pulse"), 200);
    }
  });
}

const GOV_INPUT = {
  width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 8,
  border: "1px solid #e2e8f0", fontSize: 13, fontFamily: "inherit", background: "#fff", color: "#0f172a",
};
// Leaf folder name from a full path ("/A/B/C" → "C") to keep the chip compact.
function govLeaf(p) { const s = String(p || "").split("/").filter(Boolean); return s.length ? s[s.length - 1] : p; }

export default function DocumentDetails() {
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const {
    selectedDoc,
    recentDocs = [],
    runSearch,
    selectDocument,
    clearRecentDocs,
    openFlowInCenter,
    pinnedForCompare,
    pinForCompare,
    unpinForCompare,
    isPinnedForCompare,
    dbMeta,
    setBrowseFolderId,
  } = useSearch();

  const CORE_API_BASE = useMemo(() => getApiBase(), []);

  const { hasFeature, featuresResolved } = useLicense();
  const govEnabled = hasFeature("content-governance");
  // hasFeature() answers optimistically until GET /api/license/status lands, which is right for
  // RENDERING (no flicker for a licensed customer) and wrong for FETCHING: at a site without
  // content-governance every mount fired governance/workflow/version calls that could only 403.
  // Gate the requests on the resolved answer; the strips and tabs still key off govEnabled.
  const govFetchReady = govEnabled && featuresResolved;
  // Delivery is its own sellable module, so it is gated separately from content-governance.
  const deliveryEnabled = hasFeature("delivery-portal");
  const [deliverOpen, setDeliverOpen] = useState(false);

  // Per-document content-governance status (folder / classification label / legal-hold) shown in a
  // strip under the header. Read-only, fetched lazily per selected document, only when licensed.
  const [gov, setGov] = useState(null); // { folderId, folderPath, classification, legalHold } | null
  useEffect(() => {
    if (!govFetchReady || !selectedDoc || !token) { setGov(null); return; }
    const dbName = getValueCI(selectedDoc, ["dbName", "database", "db"]);
    const docId = getValueCI(selectedDoc, ["id", "Id"]);
    if (!dbName || docId == null || docId === "") { setGov(null); return; }
    let cancelled = false;
    setGov(null);
    (async () => {
      try {
        const url = `${CORE_API_BASE}/api/Document/GovernanceStatus?databaseName=${encodeURIComponent(dbName)}&documentId=${encodeURIComponent(docId)}`;
        const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!resp.ok) { if (!cancelled) setGov(null); return; }
        const data = await resp.json();
        if (!cancelled) setGov(data);
      } catch {
        if (!cancelled) setGov(null);
      }
    })();
    return () => { cancelled = true; };
  }, [govFetchReady, selectedDoc, token, CORE_API_BASE]);

  const reloadGov = useCallback(async () => {
    if (!govFetchReady || !selectedDoc || !token) return;
    const dbName = getValueCI(selectedDoc, ["dbName", "database", "db"]);
    const docId = getValueCI(selectedDoc, ["id", "Id"]);
    if (!dbName || docId == null || docId === "") return;
    try {
      const url = `${CORE_API_BASE}/api/Document/GovernanceStatus?databaseName=${encodeURIComponent(dbName)}&documentId=${encodeURIComponent(docId)}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (resp.ok) setGov(await resp.json());
    } catch { /* keep prior */ }
  }, [govFetchReady, selectedDoc, token, CORE_API_BASE]);

  // Admin-only inline governance management (assign folder / set classification label). Writes reuse
  // the admin endpoints, which independently enforce the DIXI_ADMIN/Admin/OrgAdmin role (403 otherwise),
  // so this client gate is display-only.
  const isAdmin = !!user?.isAdmin;
  const canManageGov = govEnabled && isAdmin;
  // Same split as govFetchReady: show the admin controls optimistically, ask the server only once
  // the licence answer is in.
  const canManageGovFetch = govFetchReady && isAdmin;
  const [govFolders, setGovFolders] = useState([]);
  const [govDbMap, setGovDbMap] = useState(null); // Map(nameLower -> databaseId)
  useEffect(() => {
    if (!canManageGovFetch || !token) return;
    getFolders(token).then((l) => setGovFolders(Array.isArray(l) ? l.filter((f) => f.isActive) : [])).catch(() => {});
    getDatabases(token).then((d) => {
      const arr = Array.isArray(d) ? d : (d?.databases ?? d?.Databases ?? []);
      const m = new Map();
      arr.forEach((x) => { const nm = x.name ?? x.dbName ?? x.DbName; const id = x.id ?? x.Id; if (nm != null && id != null) m.set(String(nm).toLowerCase(), id); });
      setGovDbMap(m);
    }).catch(() => {});
  }, [canManageGovFetch, token]);

  const [manageOpen, setManageOpen] = useState(false);
  const [mgFolder, setMgFolder] = useState("");
  const [mgLabel, setMgLabel] = useState("");
  const [mgSaving, setMgSaving] = useState(false);
  const [mgError, setMgError] = useState("");
  useEffect(() => {
    setManageOpen(false);
    setMgFolder(gov?.folderId != null ? String(gov.folderId) : "");
    setMgLabel(gov?.classification || "");
    setMgError("");
  }, [selectedDoc, gov?.folderId, gov?.classification]);

  const saveGov = useCallback(async () => {
    const dbName = getValueCI(selectedDoc, ["dbName", "database", "db"]);
    const dbId = dbName && govDbMap ? govDbMap.get(String(dbName).toLowerCase()) : null;
    const archiveNo = getValueCI(selectedDoc, ["customerNo", "archiveNo", "archive_no"]);
    if (dbId == null || !archiveNo) { setMgError(t("governance.noDbId", { defaultValue: "Cannot resolve document identity." })); return; }
    setMgSaving(true); setMgError("");
    try {
      const targetFolder = mgFolder ? Number(mgFolder) : null;
      if ((gov?.folderId ?? null) !== targetFolder) {
        await setDocumentFolder(token, { databaseId: dbId, archiveNo, folderId: targetFolder });
      }
      const targetLabel = mgLabel.trim();
      if ((gov?.classification || "") !== targetLabel) {
        await overrideDocumentClassification(token, { databaseId: dbId, archiveNo, label: targetLabel || null, note: "search detail pane" });
      }
      await reloadGov();
      setManageOpen(false);
    } catch (e) {
      setMgError(e?.message || t("governance.saveFailed", { defaultValue: "Save failed." }));
    } finally { setMgSaving(false); }
  }, [selectedDoc, govDbMap, mgFolder, mgLabel, gov, token, reloadGov, t]);

  // ── Workflow cases against this document (gated content-governance) ─────────────────
  //
  // READ is deliberately available to everyone who can see the document: the endpoint lives on the non-admin
  // /api/Workflow surface so an ordinary user can tell that a document is awaiting approval — and, if they are
  // the one holding it up, be told so here rather than only in My Tasks. STARTING a case stays admin-only,
  // exactly like the folder/classification controls above; the client gate is display-only because the admin
  // endpoint independently enforces the role.
  const docDbName = getValueCI(selectedDoc, ["dbName", "database", "db"]);
  const docArchiveNo = getValueCI(selectedDoc, ["customerNo", "archiveNo", "archive_no"]);

  const [wfCases, setWfCases] = useState(null); // { items: [...] } | null
  const [wfError, setWfError] = useState("");

  const loadWorkflow = useCallback(async () => {
    if (!govFetchReady || !token || !docDbName || !docArchiveNo) { setWfCases(null); return; }
    try {
      setWfCases(await getWorkflowForDocument(token, docDbName, docArchiveNo));
    } catch {
      // A document with no workflow history is the ordinary case, so a failure here must stay quiet —
      // the section simply does not appear rather than shouting at somebody just reading a document.
      setWfCases(null);
    }
  }, [govFetchReady, token, docDbName, docArchiveNo]);

  useEffect(() => { setWfError(""); loadWorkflow(); }, [loadWorkflow]);

  // Admin-only: the definitions that can be started, and the act of starting one.
  const [wfDefs, setWfDefs] = useState([]);
  const [wfPick, setWfPick] = useState("");
  const [wfStarting, setWfStarting] = useState(false);

  useEffect(() => {
    if (!canManageGovFetch || !token) return;
    getWorkflowDefinitions(token, "Active")
      .then((d) => setWfDefs(Array.isArray(d) ? d : (d?.items ?? [])))
      .catch(() => setWfDefs([]));
  }, [canManageGovFetch, token]);

  const startWorkflow = useCallback(async () => {
    const dbId = docDbName && govDbMap ? govDbMap.get(String(docDbName).toLowerCase()) : null;
    if (!wfPick || dbId == null || !docArchiveNo) {
      setWfError(t("governance.noDbId", { defaultValue: "Cannot resolve document identity." }));
      return;
    }
    setWfStarting(true); setWfError("");
    try {
      await startWorkflowInstance(token, {
        definitionId: Number(wfPick),
        dbInfoId: dbId,
        archiveNo: String(docArchiveNo),
        subjectTitle: getValueCI(selectedDoc, ["originalName", "fileName", "name"]) || String(docArchiveNo),
      });
      setWfPick("");
      await loadWorkflow();
    } catch (e) {
      setWfError(e?.message || t("workflowDoc.startFailed", { defaultValue: "The workflow could not be started." }));
    } finally { setWfStarting(false); }
  }, [wfPick, docDbName, docArchiveNo, govDbMap, token, selectedDoc, loadWorkflow, t]);

  // ── Document versioning (gated content-governance) ──────────────────────────────────
  const [versions, setVersions] = useState(null); // { versioned, count, versions:[] } | null
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionBusy, setVersionBusy] = useState(false);
  const [versionError, setVersionError] = useState("");
  const [versionComment, setVersionComment] = useState("");

  const loadVersions = useCallback(async () => {
    if (!govFetchReady || !selectedDoc || !token) { setVersions(null); return; }
    const dbName = getValueCI(selectedDoc, ["dbName", "database", "db"]);
    const docId = getValueCI(selectedDoc, ["id", "Id"]);
    if (!dbName || docId == null) { setVersions(null); return; }
    setVersionsLoading(true); setVersionError("");
    try {
      const data = await getDocumentVersions(token, dbName, docId);
      setVersions(data || null);
    } catch (e) {
      setVersions(null);
      setVersionError(e?.message || "");
    } finally { setVersionsLoading(false); }
  }, [govFetchReady, selectedDoc, token]);

  useEffect(() => { setVersionComment(""); loadVersions(); }, [loadVersions]);

  const currentUserName = user?.userName || user?.username || user?.name || null;
  const currentVersionRow = useMemo(
    () => (versions?.versions || []).find((v) => v.isCurrent) || null,
    [versions]
  );
  const lockedBy = currentVersionRow?.checkedOutBy || null;
  const lockedByMe = !!lockedBy && !!currentUserName && String(lockedBy) === String(currentUserName);

  const runVersionAction = useCallback(async (fn) => {
    setVersionBusy(true); setVersionError("");
    try { await fn(); await loadVersions(); }
    catch (e) { setVersionError(e?.message || t("versions.actionFailed", { defaultValue: "Action failed." })); }
    finally { setVersionBusy(false); }
  }, [loadVersions, t]);

  const doCheckOut = useCallback(() => {
    const dbName = getValueCI(selectedDoc, ["dbName", "database", "db"]);
    const docId = getValueCI(selectedDoc, ["id", "Id"]);
    return runVersionAction(() => checkOutVersion(token, dbName, docId));
  }, [selectedDoc, token, runVersionAction]);

  const doCancelCheckOut = useCallback(() => {
    const dbName = getValueCI(selectedDoc, ["dbName", "database", "db"]);
    const docId = getValueCI(selectedDoc, ["id", "Id"]);
    return runVersionAction(() => cancelCheckOutVersion(token, dbName, docId));
  }, [selectedDoc, token, runVersionAction]);

  const doRestore = useCallback((versionRow) => {
    const dbName = getValueCI(selectedDoc, ["dbName", "database", "db"]);
    return runVersionAction(() => restoreDocumentVersion(token, dbName, versionRow.id,
      t("versions.restoredFrom", { defaultValue: "Restored from v{{n}}", n: versionRow.versionNumber })));
  }, [selectedDoc, token, runVersionAction, t]);

  const doUploadVersion = useCallback((file) => {
    if (!file) return;
    const dbName = getValueCI(selectedDoc, ["dbName", "database", "db"]);
    const docId = getValueCI(selectedDoc, ["id", "Id"]);
    return runVersionAction(() => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error(t("versions.readFailed", { defaultValue: "Could not read the file." })));
      reader.onload = () => {
        try {
          const b64 = String(reader.result).split(",")[1] || "";
          resolve(uploadDocumentVersion(token, {
            databaseName: dbName, documentId: docId, fileName: file.name,
            fileBase64: b64, comment: versionComment.trim() || null,
          }));
        } catch (e) { reject(e); }
      };
      reader.readAsDataURL(file);
    }));
  }, [selectedDoc, token, runVersionAction, versionComment, t]);

  // View a specific version through the SAME render pipeline as the normal viewer (per-version encrypted
  // link → PDF when the server reports that conversion is available for THIS row, converting AFP/PS
  // server-side) so non-browser-native formats render inline. A version uploaded as raw bytes (any
  // file type) reports no convertible formats, so it is fetched and labelled as its own type.
  const doViewVersion = useCallback(async (versionRow) => {
    const link = versionRow?.link;
    const vname = `${versionRow.fileName || getDocName(selectedDoc)} (v${versionRow.versionNumber})`;
    if (!link) { setVersionError(t("versions.viewFailed", { defaultValue: "Could not open this version." })); return; }
    setVersionBusy(true); setVersionError("");
    try {
      const resp = await fetch(`${CORE_API_BASE}/api/Document/GetDocumentRenderFormats`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ requests: [{ requestType: 0, link }] }),
      });
      if (!resp.ok) {
        let msg = `HTTP ${resp.status}`;
        try { const j = await resp.json(); if (j?.message) msg = j.message; } catch {}
        throw new Error(msg);
      }
      const data = await resp.json();
      const allowed = (data?.allowedFormats || []).map((f) => String(f).toUpperCase());
      const blob = await fetchDocBlob(link, allowed.includes("PDF") ? "PDF" : "Native", versionRow.fileName);
      const url = URL.createObjectURL(blob);
      setViewerState({ url, name: vname });
    } catch (e) {
      setVersionError(e?.message || t("versions.viewFailed", { defaultValue: "Could not open this version." }));
    } finally { setVersionBusy(false); }
  }, [selectedDoc, token, CORE_API_BASE, t]);

  const [flows, setFlows] = useState(() => FlowStore.loadAll());

  // Side-panel tab: "ai" | "governance" | "meta" (action buttons sit above the tabs, on every tab).
  // null = follow the default priority (see effectiveTab); set explicitly when the user clicks a tab.
  const [activeTab, setActiveTab] = useState(null);

  // Comment count for the tab badge. Owned here rather than in the panel so the number is visible on the
  // tab strip without the Discussion tab having been opened; the panel reports it back after each load.
  const [commentCount, setCommentCount] = useState(0);
  useEffect(() => { setCommentCount(0); }, [selectedDoc]);

  // Annotation viewer. Deliberately a SEPARATE surface from the plain viewer above rather than a mode of
  // it: the ordinary "view this document" path works for every format in the archive and is not worth
  // risking for a feature that only applies to PDFs.
  // Internal sharing. Sits beside Deliver deliberately: that one sends a document OUT of the
  // organisation through the portal, this one points a colleague who already has a login at it.
  const [shareOpen, setShareOpen] = useState(false);

  const [annotateState, setAnnotateState] = useState(null); // { url, name } | null

  // Whether to OFFER annotation. The server decides authoritatively (ANNOT_NOT_PDF) — this only keeps a
  // button off screens where it could not work. An AnyType database ("-") holds whatever was archived, so
  // the document's own extension is what counts, with the database's as the fallback.
  const canAnnotate = useMemo(() => {
    if (!govEnabled || !selectedDoc) return false;
    let raw = getValueCI(selectedDoc, ["fileExtension", "FileExtension"]) || "";
    if (!String(raw).trim()) {
      // dbMeta is the raw GetDatabases array, matched by name the same way buildFieldMeta does it.
      const dbName = String(getValueCI(selectedDoc, ["dbName", "database"]) || "").toLowerCase();
      const entry = Array.isArray(dbMeta)
        ? dbMeta.find((d) => String(d?.name || "").toLowerCase() === dbName
                          || String(d?.displayName || "").toLowerCase() === dbName)
        : null;
      raw = entry?.fileExtension || entry?.FileExtension || "";
    }
    return String(raw).trim().replace(/^\./, "").toLowerCase() === "pdf";
  }, [govEnabled, selectedDoc, dbMeta]);
  useEffect(() => () => { if (annotateState) URL.revokeObjectURL(annotateState.url); }, [annotateState]);

  const [viewerState, setViewerState] = useState(null); // { url, name } | null
  const [formatPicker, setFormatPicker] = useState(null); // { mode:'view'|'download', item, formats, loading, error } | null

  // AI summary state
  const [aiSummary, setAiSummary] = useState(null);      // { summary: string[], extractedData: {}, model: string }
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");

  const isAiEnabled = useMemo(() => {
    try {
      const getBool = (k) => {
        const v = localStorage.getItem(k);
        if (!v) return false;
        const s = v.toLowerCase();
        return s === "1" || s === "true" || s === "yes" || s === "on";
      };
      return getBool("use_server_llm") || getBool("use_openai");
    } catch {
      return false;
    }
  }, []);

  // Visible tabs and default selection: AI (if enabled) → Governance (if enabled) →
  // Discussion (if enabled) → Meta (always). Discussion sits after Governance because it is part of the
  // same governed-content tier and rides the same licence key, and before Meta because a conversation is
  // read far more often than a field list.
  const availableTabs = useMemo(() => {
    const tabs = [];
    if (isAiEnabled) tabs.push("ai");
    if (govEnabled) tabs.push("governance");
    if (govEnabled) tabs.push("discussion");
    tabs.push("meta");
    return tabs;
  }, [isAiEnabled, govEnabled]);
  // Fall back to the first available tab when none is chosen yet or the chosen one isn't available.
  const effectiveTab = (activeTab && availableTabs.includes(activeTab)) ? activeTab : availableTabs[0];

  const fetchAiSummary = useCallback(async (doc) => {
    if (!doc || !isAiEnabled || !token) return;

    setAiLoading(true);
    setAiError("");
    setAiSummary(null);

    try {
      // Build a curated, readable metadata object for the AI.
      // Skip encrypted/system fields (itemId, storageFileId, etc.) that add noise.
      const SKIP_KEYS = new Set([
        "itemid", "link", "storagefileid", "dbinfoid", "filetypeid",
        "id", "isprocessed", "isdeleted", "isclosed",
      ]);
      // Helper: case-insensitive field lookup
      const getCI = (keys) => {
        for (const k of keys) {
          const entry = Object.entries(doc).find(([dk]) => dk.toLowerCase() === k.toLowerCase());
          if (entry && entry[1] != null && entry[1] !== "") return String(entry[1]);
        }
        return null;
      };
      // Curated fields with human-readable labels (priority order within each group)
      const metadata = {};
      const v_fileName    = getCI(["originalName","fileName","name"]);           if (v_fileName)    metadata.fileName    = v_fileName;
      const v_ext         = getCI(["fileExtension","extension"]);                if (v_ext)         metadata.fileExtension = v_ext.toLowerCase().replace(/^\./, "");
      const v_db          = getCI(["dbName","database","db"]);                   if (v_db)          metadata.database    = v_db;
      const v_archive     = getCI(["archiveNo","archive_no","archiveNumber"]);   if (v_archive)     metadata.archiveNo   = v_archive;
      const v_date        = getCI(["documentDate","date","createdAt","created"]);if (v_date)        metadata.documentDate = v_date;
      const v_pages       = getCI(["pageCount","pages"]);                        if (v_pages)       metadata.pageCount   = v_pages;
      const v_account     = getCI(["accountNo","account","accountNumber"]);      if (v_account)     metadata.accountNo   = v_account;
      const v_docType     = getCI(["documentType","docType"]);                   if (v_docType)     metadata.documentType = v_docType;
      // Include any additional non-system, non-object fields not already captured
      const capturedKeys = new Set(["originalname","filename","name","fileextension","extension",
        "dbname","database","db","archiveno","archive_no","archivenumber","documentdate","date",
        "createdat","created","pagecount","pages","accountno","account","accountnumber",
        "documenttype","doctype"]);
      for (const [k, v] of Object.entries(doc)) {
        if (v != null && typeof v !== "object" && v !== ""
            && !SKIP_KEYS.has(k.toLowerCase())
            && !capturedKeys.has(k.toLowerCase())) {
          metadata[k] = String(v);
        }
      }

      // Get the document link for content retrieval
      const link = getValueCI(doc, ["itemId", "ItemId", "link", "Link"]) || "";

      const resp = await fetch(`${CORE_API_BASE}/api/AiAssist/summarize`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ link, metadata }),
      });

      if (resp.status === 503) {
        setAiError("ai_not_configured");
        return;
      }
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.message || `HTTP ${resp.status}`);
      }

      const data = await resp.json();
      setAiSummary({
        summary: (data.summary || []).slice(0, 4),
        extractedData: data.extractedData || {},
        model: data.model || "",
      });
    } catch (e) {
      setAiError(e.message || "Failed to generate AI summary.");
    } finally {
      setAiLoading(false);
    }
  }, [isAiEnabled, token, CORE_API_BASE]);

  useEffect(() => {
    setViewError("");
    setAiSummary(null);
    setAiError("");
    if (viewerState) { URL.revokeObjectURL(viewerState.url); setViewerState(null); }
    if (selectedDoc && isAiEnabled) {
      fetchAiSummary(selectedDoc);
    }
  }, [selectedDoc, isAiEnabled, fetchAiSummary]);

  useEffect(() => {
    if (!token) return;
    getFlows(token)
      .then((serverFlows) => {
        if (Array.isArray(serverFlows) && serverFlows.length > 0) {
          FlowStore.syncFromServer(serverFlows);
          setFlows(serverFlows);
        }
      })
      .catch(() => {});
  }, [token]);

  // nameHint: the file name the bytes actually belong to. Needed for version views, where the row
  // being fetched can be a different type from the selected (current) document — falling back to
  // selectedDoc's extension would mislabel the blob.
  const fetchDocBlob = async (link, outputFormat, nameHint) => {
    const resp = await fetch(`${CORE_API_BASE}/api/Document/ViewDocument`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ requests: [{ requestType: 0, link, outputFormat: outputFormat === "Native" ? null : (outputFormat || null) }] }),
    });
    if (!resp.ok) {
      let msg = `HTTP ${resp.status}`;
      try { const j = await resp.json(); if (j?.message) msg = j.message; } catch {}
      throw new Error(msg);
    }
    // Prefer the server-detected Content-Type (set from magic bytes on the backend)
    // over guessing from the requested format. This prevents creating a fake application/pdf
    // blob when the Gatherer returns AFP bytes (e.g. conversion not configured).
    const serverContentType = resp.headers.get("Content-Type") || "";
    const hintExt = nameHint && String(nameHint).includes(".") ? String(nameHint).split(".").pop() : "";
    const fmtKey = ((outputFormat && outputFormat !== "Native")
      ? outputFormat
      : hintExt || getValueCI(selectedDoc, ["fileExtension", "FileExtension"]) || "").toLowerCase().replace(/^\./, "");
    const mime =
      (serverContentType && !serverContentType.startsWith("application/octet-stream"))
        ? serverContentType.split(";")[0].trim()
        : MIME_MAP[fmtKey] || "application/octet-stream";
    const arrayBuffer = await resp.arrayBuffer();
    return new Blob([arrayBuffer], { type: mime });
  };

  const openFormatPicker = async (mode) => {
    const rawLink = getValueCI(selectedDoc, ["itemId", "ItemId", "link", "Link"]);
    setFormatPicker({ mode, item: selectedDoc, formats: null, loading: true, error: rawLink ? null : "No document link available." });
    if (!rawLink) return;
    try {
      const resp = await fetch(`${CORE_API_BASE}/api/Document/GetDocumentRenderFormats`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ requests: [{ requestType: 0, link: rawLink }] }),
      });
      if (!resp.ok) {
        let msg = `HTTP ${resp.status}`;
        try { const j = await resp.json(); if (j?.message) msg = j.message; } catch {}
        setFormatPicker((prev) => prev ? { ...prev, loading: false, error: msg } : null);
        return;
      }
      const data = await resp.json();
      setFormatPicker((prev) => prev ? { ...prev, formats: data, loading: false } : null);
    } catch (err) {
      setFormatPicker((prev) => prev ? { ...prev, loading: false, error: err?.message || "Failed to load formats." } : null);
    }
  };

  const handleFormatSelected = async (item, outputFormat) => {
    const rawLink = getValueCI(item, ["itemId", "ItemId", "link", "Link"]);
    const mode = formatPicker?.mode;
    setFormatPicker((prev) => prev ? { ...prev, loading: true, error: null } : null);
    try {
      const blob = await fetchDocBlob(rawLink, outputFormat);
      const url = URL.createObjectURL(blob);
      setFormatPicker(null);
      if (mode === "view") {
        setViewerState({ url, name: getDocName(item) });
      } else {
        const a = document.createElement("a");
        a.href = url;
        const baseName = getDocName(item);
        const nativeTypeId = formatPicker?.formats?.nativeTypeId || "";
        const ext = ((outputFormat && outputFormat !== "Native")
          ? outputFormat
          : (nativeTypeId || getValueCI(item, ["fileExtension", "FileExtension"]) || ""))
          .toString().toLowerCase().replace(/^\./, "");
        const nameWithoutExt = baseName.replace(/\.[A-Za-z0-9]{2,5}$/, "");
        a.download = ext ? `${nameWithoutExt}.${ext}` : baseName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      }
    } catch (e) {
      setFormatPicker((prev) => prev ? { ...prev, loading: false, error: e.message || "Failed to load document." } : null);
    }
  };

  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState("");

  const handleViewDocument = async () => {
    const rawLink = getValueCI(selectedDoc, ["itemId", "ItemId", "link", "Link"]);
    if (!rawLink) { setViewError("No document link available."); return; }
    setViewLoading(true);
    setViewError("");
    try {
      const resp = await fetch(`${CORE_API_BASE}/api/Document/GetDocumentRenderFormats`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ requests: [{ requestType: 0, link: rawLink }] }),
      });
      if (!resp.ok) {
        let msg = `HTTP ${resp.status}`;
        try { const j = await resp.json(); if (j?.message) msg = j.message; } catch {}
        setViewError(msg);
        return;
      }
      const data = await resp.json();
      const allowedFormats = (data?.allowedFormats || []).map((f) => f.toUpperCase());
      if (allowedFormats.includes("PDF")) {
        // PDF available — open directly, no modal
        const blob = await fetchDocBlob(rawLink, "PDF");
        const url = URL.createObjectURL(blob);
        setViewerState({ url, name: getDocName(selectedDoc) });
      } else {
        // No PDF option — show the picker (already have formats, no extra loading state)
        setFormatPicker({ mode: "view", item: selectedDoc, formats: data, loading: false, error: null });
      }
    } catch (err) {
      setViewError(err?.message || "Failed to load document.");
    } finally {
      setViewLoading(false);
    }
  };

  // Opening the annotator fetches the SAME PDF rendering the viewer would show, then hands it to pdf.js.
  // Nothing is written back: the marks live in their own table, and this blob is read-only.
  const handleAnnotate = async () => {
    const rawLink = getValueCI(selectedDoc, ["itemId", "ItemId", "link", "Link"]);
    if (!rawLink) { setViewError(t("annotations.noLink", { defaultValue: "No document link available." })); return; }
    setViewLoading(true);
    setViewError("");
    try {
      const blob = await fetchDocBlob(rawLink, "PDF");
      setAnnotateState({ url: URL.createObjectURL(blob), name: getDocName(selectedDoc) });
    } catch (err) {
      setViewError(err?.message || t("annotations.openFailed", { defaultValue: "The document could not be opened for annotation." }));
    } finally {
      setViewLoading(false);
    }
  };

  const handleDownload = () => openFormatPicker("download");

  const pickIcon = (f) => {
    if (!f) return "default";
    if (f.icon) return String(f.icon).toLowerCase();
    const name = (f.name || "").toLowerCase();
    const labels = (f.steps || []).map((s) => (s.label || "").toLowerCase()).join(" ");
    if (/invoice|bill|payment/.test(name + " " + labels)) return "invoice";
    if (/bank|account|customer|card|cards|credit/.test(name + " " + labels)) return "bank";
    if (/archive|archive no|archiv/.test(name + " " + labels)) return "archive";
    if (/export|metadata|json|copy/.test(name + " " + labels)) return "export";
    if (/search|find|lookup/.test(name + " " + labels)) return "search";
    return "default";
  };

  // FieldInfo metadata for the document's base (display names + data types + field
  // order for the customer's index columns). GetDatabases already ships it as
  // `relatedIndexes`, cached in the search context — no extra request, and it is
  // what lets the Meta tab tell customer index columns from storage plumbing.
  const fieldMeta = useMemo(
    () => buildFieldMeta(dbMeta, getValueCI(selectedDoc, ["dbName", "database", "db"])),
    [dbMeta, selectedDoc],
  );

  // Heuristically locate a search-indexes object or JSON string
  const indexes = useMemo(() => {
    const doc = selectedDoc || {};
    const candidates = Object.keys(doc || {}).filter((k) =>
      /(index|indexes|indices|metadata|props|properties)/i.test(k),
    );
    for (const k of candidates) {
      const v = doc[k];
      if (v && typeof v === "object" && !Array.isArray(v))
        return { key: k, obj: v };
      if (typeof v === "string" && v.trim().startsWith("{")) {
        try {
          return { key: k, obj: JSON.parse(v) };
        } catch {}
      }
    }
    return null;
  }, [selectedDoc]);

  // Opening a folder from the summary drives the CENTRE pane's folder browse. Both halves are needed:
  // the shared browse id is what the centre reads, and the raw filter is what actually loads the
  // documents — setting the id alone would leave the centre showing an empty folder.
  const openOrgFolderInCentre = useCallback((id) => {
    if (setBrowseFolderId) setBrowseFolderId(id);
    if (runSearch) runSearch(JSON.stringify({ FolderId: { $eq: id } }));
  }, [setBrowseFolderId, runSearch]);

  /* ─── Empty state ─────────────────────────────────────────────── */
  if (!selectedDoc) {
    return (
      <aside className="detail-panel">
        <ResizeHandle />
        <div className="dp-empty">
          {/* The summary's LISTS stay here, where they have been: what is waiting on you, what you
              started, your folders. Only the greeting and the counters moved to the top of the centre
              pane. They open the panel rather than a "no document selected" placard — with real work
              on screen, saying the panel is empty is both untrue and a waste of the first screenful.
              Without content-governance there are no lists, and the placard is what the panel has. */}
          {govEnabled ? (
            <HomeDashboard
              variant="panels"
              workflowEnabled={govEnabled}
              onOpenFolder={openOrgFolderInCentre}
            />
          ) : (
            <div className="dp-empty-hero">
              {Icons.doc}
              <div style={{ fontWeight: 600 }}>{t("detail.noDocument")}</div>
              <div style={{ fontSize: 13 }}>{t("detail.selectDocumentPrompt")}</div>
            </div>
          )}

          {/* Recently viewed */}
          {recentDocs && recentDocs.length > 0 && (
            <div className="dp-recent-section">
              <div className="dp-recent-title">{t("detail.recentlyViewed")}</div>
              <div className="dp-recent-list">
                {recentDocs.slice(0, 5).map((r, i) => (
                  <button
                    key={r.id || r.ts || i}
                    className="dp-recent-item"
                    onClick={() => selectDocument(r.raw)}
                    type="button"
                  >
                    <div className="dp-recent-thumb" aria-hidden>
                      <div className="line" style={{ width: "80%" }} />
                      <div className="line" style={{ width: "60%" }} />
                    </div>
                    <div className="dp-recent-info">
                      <div className="dp-recent-name">{r.title}</div>
                      <div className="dp-recent-date">
                        {r.date ? String(r.date).slice(0, 10) : ""}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="dp-quick-tips">
            <div className="dp-quick-tips-title">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/></svg>
              {t("detail.quickTips", { defaultValue: "Quick Tips" })}
            </div>
            <ul className="dp-quick-tips-list">
              <li>{t("detail.tipClickRow", { defaultValue: "Click any row to see its details here" })}</li>
              <li>{t("detail.tipCheckbox", { defaultValue: "Use checkboxes to select multiple documents" })}</li>
              <li>{t("detail.tipShiftClick", { defaultValue: "Hold Shift+Click to select a range" })}</li>
              <li>{t("detail.tipKeyboard", { defaultValue: "Use the search bar to find documents by any field" })}</li>
            </ul>
          </div>
        </div>
      </aside>
    );
  }

  /* ─── Selected document state ─────────────────────────────────── */
  const name = getDocName(selectedDoc);
  // Tolerate legacy/mixed values: strip any leading dot(s) and a stray "-" placeholder
  // so ".afp", "afp", and "-" all render uniformly (and "-" reads as no extension).
  const rawExt = (getValueCI(selectedDoc, ["fileExtension", "extension", "type"]) || "").toString().trim();
  const ext = (rawExt === "-" ? "" : rawExt.replace(/^\.+/, "")).toUpperCase();
  const date = getDocDate(selectedDoc);
  const pages = getPageCount(selectedDoc);
  const account = getValueCI(selectedDoc, [
    "archiveNo", "archiveno", "archive", "account", "customerNo", "customerNumber",
  ]);

  // Split name at last dot for rendering
  const dotIdx = name.lastIndexOf(".");
  const baseName = dotIdx > 0 ? name.slice(0, dotIdx) : name;
  const extPart = dotIdx > 0 ? name.slice(dotIdx) : "";

  return (
    <aside className="detail-panel">
      <ResizeHandle />

      {/* Back button */}
      <div className="dp-back">
        <button onClick={() => selectDocument(null)}>{Icons.back}</button>
      </div>

      {/* Document header */}
      <div className="dp-header">
        <div className="dp-doc-name">
          <span>{baseName}<span className="ext">{extPart}</span></span>
          <button
            className="dp-copy-btn"
            title={t("common.copy", { defaultValue: "Copy name" })}
            onClick={() => { try { navigator.clipboard.writeText(name); } catch {} }}
            type="button"
          >
            {Icons.copy}
          </button>
        </div>
        <div className="dp-doc-date">{date ? fmt.date(date) : ""}</div>
      </div>

      {/* Persistent action buttons — pinned above the tabs, present on every tab */}
      <div className="dp-section dp-actions-top">
        <div className="dp-actions-row">
          <button className="dp-action-btn primary" onClick={handleViewDocument} disabled={viewLoading}>
            {viewLoading ? t("common.loading", { defaultValue: "Loading…" }) : t("detail.view", { defaultValue: "View" })}
          </button>
          <button className="dp-action-btn" onClick={handleDownload} disabled={!!formatPicker?.loading}>
            {Icons.download} {t("detail.download", { defaultValue: "Download" })}
          </button>
          {canAnnotate && (
            <button
              className="dp-action-btn"
              onClick={handleAnnotate}
              disabled={viewLoading}
              title={t("annotations.openTitle", { defaultValue: "Mark up this document. The archived file is never changed." })}
            >
              {t("annotations.open", { defaultValue: "Annotate" })}
            </button>
          )}
          {deliveryEnabled && (
            <button
              className="dp-action-btn"
              onClick={() => setDeliverOpen(true)}
              title={t("deliverModal.title", { defaultValue: "Deliver documents" })}
            >
              {t("detail.deliver", { defaultValue: "Deliver…" })}
            </button>
          )}
          {govEnabled && (
            <button
              className="dp-action-btn"
              onClick={() => setShareOpen(true)}
              title={t("share.buttonTitle", { defaultValue: "Point a colleague at this document. It does not give them access." })}
            >
              {t("share.button", { defaultValue: "Share…" })}
            </button>
          )}
          <button
            className={`dp-action-btn${isPinnedForCompare(selectedDoc) ? " pinned" : ""}`}
            onClick={(e) => {
              if (isPinnedForCompare(selectedDoc)) {
                unpinForCompare(selectedDoc);
              } else {
                flyToCompareTray(e.currentTarget);
                pinForCompare(selectedDoc);
              }
            }}
            title={isPinnedForCompare(selectedDoc)
              ? t("compare.unpin")
              : pinnedForCompare.length === 0
                ? t("compare.needTwo")
                : t("compare.compareNow")}
          >
            {Icons.compare} {t("compare.compareNow")}
          </button>
        </div>
        {viewError && (
          <div style={{ color: "#dc2626", fontSize: 12, marginTop: 8 }}>{viewError}</div>
        )}
      </div>

      {/* The personal layer against this document: which of my own folders hold it, and a way to put
          it in one. Deliberately outside the governance tabs and ungated — a person's own filing is
          not governance machinery, and it has to be visible wherever the document is or the workspace
          becomes a corner of the product you must remember to visit. */}
      <DocumentFilings
        dbName={docDbName}
        archiveNo={docArchiveNo}
        documentId={selectedDoc?.id ?? selectedDoc?.Id}
      />

      {/* Tabs: AI (if enabled) · Governance (if enabled) · Discussion (if enabled) · Meta (always). */}
      {availableTabs.length > 1 && (
        <div className="dp-tabs">
          {availableTabs.map((tab) => (
            <button key={tab} type="button" className={`dp-tab${effectiveTab === tab ? " active" : ""}`} onClick={() => setActiveTab(tab)}>
              {tab === "ai"
                ? t("detail.tabAi", { defaultValue: "AI" })
                : tab === "governance"
                  ? t("governance.title", { defaultValue: "Governance" })
                  : tab === "discussion"
                    ? (
                      <>
                        {t("discussion.title", { defaultValue: "Discussion" })}
                        {commentCount > 0 && <span className="dp-tab-count">{commentCount}</span>}
                      </>
                    )
                    : t("detail.tabMeta", { defaultValue: "Meta" })}
            </button>
          ))}
        </div>
      )}

      {/* ── Governance tab ──────────────────────────────────────────── */}
      {effectiveTab === "governance" && (<>
      {/* Content-governance — native section matching Extracted Data / Actions (muted, not colorful) */}
      {govEnabled && ((gov && (gov.folderPath || gov.classification || gov.legalHold)) || canManageGov) && (
        <div className="dp-section">
          <div className="dp-section-title">
            <span className="dp-icon data">{Icons.doc}</span>
            {t("governance.title", { defaultValue: "Governance" })}
          </div>

          <div className="dp-extracted-grid">
            <div className="dp-extracted-row">
              <span className="dp-label">{t("governance.folder", { defaultValue: "Folder" })}</span>
              <span className="dp-value" title={gov?.folderPath || ""}>{gov?.folderPath ? govLeaf(gov.folderPath) : "—"}</span>
            </div>
            <div className="dp-extracted-row">
              <span className="dp-label">{t("governance.classification", { defaultValue: "Classification" })}</span>
              <span className="dp-value">{gov?.classification || "—"}</span>
            </div>
            <div className="dp-extracted-row">
              <span className="dp-label">{t("governance.onHold", { defaultValue: "Legal hold" })}</span>
              <span className="dp-value">
                {gov?.legalHold ? (
                  <>
                    <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#dc2626", display: "inline-block", marginTop: 5 }} />
                    <span style={{ color: "#b91c1c" }}>{t("governance.holdActive", { defaultValue: "On hold" })}</span>
                  </>
                ) : t("governance.holdNone", { defaultValue: "None" })}
              </span>
            </div>
          </div>

          {canManageGov && manageOpen && (
            <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
              <div>
                <div className="dp-label" style={{ marginBottom: 4 }}>{t("governance.folder", { defaultValue: "Folder" })}</div>
                <select value={mgFolder} onChange={(e) => setMgFolder(e.target.value)} style={GOV_INPUT}>
                  <option value="">{t("governance.noFolder", { defaultValue: "(none)" })}</option>
                  {govFolders.map((f) => <option key={f.id} value={f.id}>{f.path || f.name}</option>)}
                </select>
              </div>
              <div>
                <div className="dp-label" style={{ marginBottom: 4 }}>{t("governance.classification", { defaultValue: "Classification" })}</div>
                <input value={mgLabel} onChange={(e) => setMgLabel(e.target.value)} placeholder={t("governance.labelPlaceholder", { defaultValue: "e.g. PII, Confidential" })} style={GOV_INPUT} />
              </div>
              {mgError && <div style={{ color: "#dc2626", fontSize: 12 }}>{mgError}</div>}
            </div>
          )}

          {canManageGov && (
            manageOpen ? (
              <div className="dp-actions-row" style={{ marginTop: 14 }}>
                <button type="button" className="dp-action-btn primary" disabled={mgSaving} onClick={saveGov}>
                  {mgSaving ? t("common.loading", { defaultValue: "Loading…" }) : t("governance.save", { defaultValue: "Save" })}
                </button>
                <button type="button" className="dp-action-btn" onClick={() => setManageOpen(false)}>
                  {t("governance.cancel", { defaultValue: "Cancel" })}
                </button>
              </div>
            ) : (
              <div className="dp-actions-row" style={{ marginTop: 14 }}>
                <button type="button" className="dp-action-btn primary" style={{ flex: "0 0 auto" }} onClick={() => setManageOpen(true)}>
                  {t("governance.manage", { defaultValue: "Manage" })}
                </button>
                <Link className="dp-action-btn" style={{ textDecoration: "none" }} to="/admin/legal-holds" title={t("holds.title", { defaultValue: "Legal Holds" })}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                  {t("governance.linkHolds", { defaultValue: "Holds" })}
                </Link>
                <Link className="dp-action-btn" style={{ textDecoration: "none" }} to="/admin/disposition" title={t("disposition.title", { defaultValue: "Disposition" })}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M6 6l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14"/></svg>
                  {t("governance.linkDisposition", { defaultValue: "Dispose" })}
                </Link>
                <Link className="dp-action-more" style={{ textDecoration: "none", color: "#6b7280" }} to="/admin/content-organization" title={t("contentOrg.title", { defaultValue: "Content Organization" })}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 6h18M3 12h18M3 18h18"/></svg>
                </Link>
              </div>
            )
          )}
        </div>
      )}

      {/* Versions — document version chain (check-out / upload / restore). Gated content-governance. */}
      {govEnabled && (
        <div className="dp-section">
          <div className="dp-section-title">
            <span className="dp-icon data">{Icons.doc}</span>
            {t("versions.title", { defaultValue: "Versions" })}
            {versions?.count > 1 && <span className="dp-ai-badge">{versions.count}</span>}
          </div>

          {versionsLoading ? (
            <div style={{ color: "#6b7280", fontSize: 13 }}>{t("common.loading", { defaultValue: "Loading…" })}</div>
          ) : (
            <>
              {lockedBy && (
                <div style={{ fontSize: 12, color: lockedByMe ? "#1d4ed8" : "#b45309", marginBottom: 10 }}>
                  {lockedByMe
                    ? t("versions.checkedOutByYou", { defaultValue: "You have this document checked out." })
                    : t("versions.checkedOutBy", { defaultValue: "Checked out by {{who}}.", who: lockedBy })}
                </div>
              )}

              <div className="dp-version-list" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {(versions?.versions || []).map((v) => (
                  <div key={v.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px",
                    border: "1px solid #e5e7eb", borderRadius: 8, background: v.isCurrent ? "#f8fafc" : "#fff" }}>
                    <span style={{ fontWeight: 600, fontSize: 13, minWidth: 34 }}>v{v.versionNumber}</span>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      title={v.comment || v.fileName || ""}>
                      {v.fileName || "—"}{v.comment ? ` · ${v.comment}` : ""}
                    </span>
                    {v.isCurrent && <span className="dp-ai-badge">{t("versions.current", { defaultValue: "Current" })}</span>}
                    <button type="button" className="dp-action-btn" style={{ flex: "0 0 auto" }}
                      disabled={versionBusy} onClick={() => doViewVersion(v)}>
                      {t("versions.view", { defaultValue: "View" })}
                    </button>
                    {!v.isCurrent && (
                      <button type="button" className="dp-action-btn" style={{ flex: "0 0 auto" }}
                        disabled={versionBusy || (lockedBy && !lockedByMe)} onClick={() => doRestore(v)}>
                        {t("versions.restore", { defaultValue: "Restore" })}
                      </button>
                    )}
                  </div>
                ))}
                {(!versions || !(versions.versions || []).length) && (
                  <div style={{ color: "#6b7280", fontSize: 13 }}>
                    {t("versions.none", { defaultValue: "No previous versions. Upload a new file to start a version history." })}
                  </div>
                )}
              </div>

              {versionError && <div style={{ color: "#dc2626", fontSize: 12, marginTop: 10 }}>{versionError}</div>}

              <div style={{ marginTop: 12 }}>
                <input value={versionComment} onChange={(e) => setVersionComment(e.target.value)}
                  placeholder={t("versions.commentPlaceholder", { defaultValue: "Optional comment for the new version" })} style={GOV_INPUT} />
              </div>

              <div className="dp-actions-row" style={{ marginTop: 12 }}>
                <label className={`dp-action-btn primary${versionBusy ? " disabled" : ""}`} style={{ flex: "0 0 auto", cursor: versionBusy ? "default" : "pointer", opacity: versionBusy ? 0.6 : 1 }}>
                  {t("versions.upload", { defaultValue: "Upload new version" })}
                  <input type="file" style={{ display: "none" }} disabled={versionBusy || (lockedBy && !lockedByMe)}
                    onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; doUploadVersion(f); }} />
                </label>
                {lockedBy ? (
                  <button type="button" className="dp-action-btn" disabled={versionBusy || (!lockedByMe && !isAdmin)} onClick={doCancelCheckOut}>
                    {t("versions.checkIn", { defaultValue: "Release check-out" })}
                  </button>
                ) : (
                  <button type="button" className="dp-action-btn" disabled={versionBusy} onClick={doCheckOut}>
                    {t("versions.checkOut", { defaultValue: "Check out" })}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Workflow — approval cases opened against this document. Read for everyone, start for admins. */}
      {govEnabled && ((wfCases?.items?.length ?? 0) > 0 || canManageGov) && (
        <div className="dp-section">
          <div className="dp-section-title">
            <span className="dp-icon data">{Icons.doc}</span>
            {t("workflowDoc.title", { defaultValue: "Workflow" })}
            {(wfCases?.items?.length ?? 0) > 0 && <span className="dp-ai-badge">{wfCases.items.length}</span>}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {(wfCases?.items || []).map((c) => (
              <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px",
                border: "1px solid #e5e7eb", borderRadius: 8, background: c.isRunning ? "#f8fafc" : "#fff" }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>{c.definitionName || "—"}</span>
                  <span style={{ display: "block", fontSize: 12, color: "#6b7280", overflow: "hidden",
                    textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {c.isRunning
                      ? (c.waitingOn?.length
                          ? t("workflowDoc.waitingOn", { defaultValue: "waiting on {{step}}", step: c.waitingOn.join(", ") })
                          : t("workflowDoc.running", { defaultValue: "in progress" }))
                      : (c.outcome || c.status)}
                    {c.startedByRule && ` · ${t("workflowDoc.byRule", { defaultValue: "started by a rule" })}`}
                  </span>
                </span>

                {/* The one chip that is actionable rather than informational. */}
                {c.awaitingMe && (
                  <Link className="dp-action-btn" style={{ flex: "0 0 auto", textDecoration: "none" }} to="/my-tasks">
                    {t("workflowDoc.awaitingMe", { defaultValue: "Your decision" })}
                  </Link>
                )}
                {c.isOverdue && (
                  <span className="dp-ai-badge" style={{ background: "#fee2e2", color: "#b91c1c" }}>
                    {t("workflowDoc.overdue", { defaultValue: "Overdue" })}
                  </span>
                )}
                {c.isRunning && !c.isOverdue && (
                  <span className="dp-ai-badge">{t("workflowDoc.open", { defaultValue: "Open" })}</span>
                )}
              </div>
            ))}

            {(wfCases?.items?.length ?? 0) === 0 && (
              <div style={{ color: "#6b7280", fontSize: 13 }}>
                {t("workflowDoc.none", { defaultValue: "No workflow has been started on this document." })}
              </div>
            )}
          </div>

          {wfError && <div style={{ color: "#dc2626", fontSize: 12, marginTop: 10 }}>{wfError}</div>}

          {canManageGov && (
            wfDefs.length > 0 ? (
              <div className="dp-actions-row" style={{ marginTop: 12 }}>
                <select value={wfPick} onChange={(e) => setWfPick(e.target.value)} style={{ ...GOV_INPUT, flex: 1 }}>
                  <option value="">{t("workflowDoc.pick", { defaultValue: "Choose a workflow…" })}</option>
                  {wfDefs.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
                <button type="button" className="dp-action-btn primary" style={{ flex: "0 0 auto" }}
                  disabled={!wfPick || wfStarting} onClick={startWorkflow}>
                  {wfStarting
                    ? t("common.loading", { defaultValue: "Loading…" })
                    : t("workflowDoc.start", { defaultValue: "Start" })}
                </button>
              </div>
            ) : (
              // Nothing ACTIVE to start: say why, rather than showing a control that can only fail.
              <div style={{ color: "#6b7280", fontSize: 12, marginTop: 12 }}>
                {t("workflowDoc.noneActive", { defaultValue: "No active workflow is available to start." })}
              </div>
            )
          )}
        </div>
      )}

      </>)}

      {/* ── AI tab ──────────────────────────────────────────────────── */}
      {effectiveTab === "ai" && (<>
      {/* AI Summary */}
      <div className="dp-section">
        <div className="dp-section-title">
          <span className="dp-icon ai">{Icons.aiDot}</span>
          {t("detail.aiSummary")}
          {aiSummary && <span className="dp-ai-badge">{t("detail.aiAnalyzed", { defaultValue: "AI Analyzed" })}</span>}
        </div>
        {!isAiEnabled ? (
          <div className="dp-ai-placeholder">
            <p style={{ color: "#6b7280", fontSize: 13, margin: 0 }}>
              {t("detail.aiNotEnabled")}
            </p>
          </div>
        ) : aiLoading ? (
          <div className="dp-ai-placeholder">
            <div className="dp-ai-loading">
              <span className="dp-spinner" />
              <span style={{ fontSize: 13, color: "#6b7280" }}>{t("detail.aiAnalyzing")}</span>
            </div>
          </div>
        ) : aiError ? (
          <div className="dp-ai-placeholder">
            {aiError === "ai_not_configured" ? (
              <p style={{ color: "#6b7280", fontSize: 13, margin: 0 }}>
                {t("detail.aiNotConfigured")}
              </p>
            ) : (
              <div className="dp-ai-info-card">
                <div className="dp-ai-info-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                </div>
                <div className="dp-ai-info-body">
                  <p className="dp-ai-info-title">{t("detail.aiServiceTitle", { defaultValue: "AI service not available" })}</p>
                  <p className="dp-ai-info-desc">{t("detail.aiServiceDesc", { defaultValue: "AI analysis is an optional feature. Document viewing, search, and all other features work normally without it." })}</p>
                  <button
                    className="dp-ai-info-retry"
                    onClick={() => fetchAiSummary(selectedDoc)}
                  >
                    {t("detail.aiRetry")}
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : aiSummary ? (
          aiSummary.summary.length > 0 ? (
            <ul className="dp-ai-bullets">
              {aiSummary.summary.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          ) : (
            <div style={{ fontSize: 13, color: "#9ca3af", padding: "4px 0" }}>
              {t("detail.aiNoContent", { defaultValue: "No extractable content found in this document." })}
            </div>
          )
        ) : null}
      </div>

      {/* Extracted Data */}
      <div className="dp-section">
        <div className="dp-section-title">
          <span className="dp-icon data">{Icons.doc}</span>
          {t("detail.extractedData")}
        </div>
        {!isAiEnabled ? (
          <div className="dp-ai-placeholder">
            <p style={{ color: "#6b7280", fontSize: 13, margin: 0 }}>
              {t("detail.aiNotEnabled")}
            </p>
          </div>
        ) : aiLoading ? (
          <div style={{ fontSize: 13, color: "#9ca3af", padding: "4px 0" }}>{t("common.loading")}</div>
        ) : aiSummary && Object.keys(aiSummary.extractedData).length > 0 ? (
          <div className="dp-extracted-grid">
            {Object.entries(aiSummary.extractedData).map(([k, v]) => (
              <div key={k} className="dp-extracted-row">
                <span className="dp-label">{k}:</span>
                <span className="dp-value">{v}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="dp-extracted-grid">
            {account && (
              <div className="dp-extracted-row">
                <span className="dp-label">{t("detail.account")}:</span>
                <span className="dp-value">{account}</span>
              </div>
            )}
            {ext && (
              <div className="dp-extracted-row">
                <span className="dp-label">{t("detail.type")}:</span>
                <span className="dp-value">.{ext}</span>
              </div>
            )}
            {pages != null && (
              <div className="dp-extracted-row">
                <span className="dp-label">{t("detail.pages")}:</span>
                <span className="dp-value">{pages}</span>
              </div>
            )}
          </div>
        )}
      </div>

      </>)}

      {/* ── Discussion tab ─────────────────────────────────────────── */}
      {effectiveTab === "discussion" && (
        <DiscussionPanel
          dbName={getValueCI(selectedDoc, ["dbName", "database"])}
          archiveNo={getValueCI(selectedDoc, [
            "archiveNo", "archiveno", "archive", "customerNo", "customerNumber",
          ])}
          documentId={getValueCI(selectedDoc, ["id", "Id"])}
          token={token}
          onCountChange={setCommentCount}
        />
      )}

      {/* ── Meta tab ────────────────────────────────────────────────── */}
      {effectiveTab === "meta" && (<>
      {/* Related */}
      <div className="dp-section">
        <div className="dp-section-title">
          <span className="dp-icon related">{Icons.people}</span>
          {t("related.title")}
        </div>
        <RelatedDocuments
          dbName={getValueCI(selectedDoc, ["dbName", "database"])}
          archiveNo={getValueCI(selectedDoc, [
            "archiveNo", "archiveno", "archive", "customerNo", "customerNumber",
          ])}
          onNavigate={(navDbName, navArchiveNo) => {
            if (runSearch) runSearch(`${navDbName} ${navArchiveNo}`);
          }}
        />
      </div>

      {/* All metadata — grouped, filterable (index fields / document / storage) */}
      <div className="dp-section">
        <div className="dp-section-title">
          <span className="dp-icon meta">{Icons.list}</span>
          {t("meta.title", { defaultValue: "Metadata" })}
        </div>
        <MetaPanel
          doc={selectedDoc}
          indexObj={indexes?.obj}
          indexKey={indexes?.key}
          fieldMeta={fieldMeta}
        />
      </div>
      </>)}

      {/* Format picker modal (Download) */}
      {formatPicker && (
        <FormatPickerModal
          item={formatPicker.item}
          formats={formatPicker.formats}
          loading={formatPicker.loading}
          error={formatPicker.error}
          onSelect={handleFormatSelected}
          onClose={() => setFormatPicker(null)}
        />
      )}

      {/* In-app Document Viewer Popup */}
      {viewerState && createPortal(
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)",
            zIndex: 3000, display: "flex", flexDirection: "column",
          }}
        >
          {/* Toolbar */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: "#1e293b", padding: "10px 16px", flexShrink: 0,
          }}>
            <span style={{
              fontSize: 14, fontWeight: 600, color: "#f1f5f9",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              maxWidth: "calc(100% - 160px)",
            }}>
              {viewerState.name}
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                type="button"
                onClick={() => {
                  const a = document.createElement("a");
                  a.href = viewerState.url;
                  a.download = viewerState.name;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                }}
                style={{
                  display: "flex", alignItems: "center", gap: 5,
                  padding: "5px 13px", borderRadius: 6, border: "none",
                  background: "#334155", color: "#f1f5f9", fontSize: 12,
                  cursor: "pointer", fontWeight: 500,
                }}
              >
                {Icons.download} Download
              </button>
              <button
                type="button"
                onClick={() => { URL.revokeObjectURL(viewerState.url); setViewerState(null); }}
                style={{
                  padding: "5px 13px", borderRadius: 6, border: "none",
                  background: "#475569", color: "#f1f5f9", fontSize: 12,
                  cursor: "pointer", fontWeight: 500,
                }}
              >
                ✕ Close
              </button>
            </div>
          </div>
          {/* Document frame */}
          <iframe
            src={viewerState.url}
            title={viewerState.name}
            style={{ flex: 1, border: "none", background: "#f8fafc" }}
          />
        </div>,
        document.body
      )}

      <ShareModal
        open={shareOpen}
        dbName={getValueCI(selectedDoc, ["dbName", "database"])}
        archiveNo={getValueCI(selectedDoc, [
          "archiveNo", "archiveno", "archive", "customerNo", "customerNumber",
        ])}
        token={token}
        onClose={() => setShareOpen(false)}
      />

      <AnnotationViewer
        open={!!annotateState}
        blobUrl={annotateState?.url}
        docName={annotateState?.name}
        dbName={getValueCI(selectedDoc, ["dbName", "database"])}
        documentId={getValueCI(selectedDoc, ["id", "Id"])}
        token={token}
        onClose={() => setAnnotateState(null)}
      />

      {deliveryEnabled && (
        <DeliverModal
          open={deliverOpen}
          dbName={getValueCI(selectedDoc, ["dbName", "database", "db"])}
          documentIds={[getValueCI(selectedDoc, ["id", "Id"])].filter((x) => x != null && x !== "")}
          onClose={() => setDeliverOpen(false)}
        />
      )}
    </aside>
  );
}

function ResizeHandle() {
  const onMouseDown = (e) => {
    e.preventDefault();
    const root = document.documentElement;
    const startX = e.clientX;
    const startW = parseInt(
      getComputedStyle(root).getPropertyValue("--detail-width").trim() || "320",
      10,
    );
    const onMove = (ev) => {
      const dx = startX - ev.clientX;
      const next = Math.min(Math.max(startW + dx, 240), 640);
      root.style.setProperty("--detail-width", next + "px");
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  return <div className="detail-resizer" onMouseDown={onMouseDown} />;
}

